import React, { useState, useEffect, useRef } from 'react'
import { Formik, FieldArray } from 'formik'
import { ExperienceCheckbox } from '../form'
import QuestHOC from './QuestHOC'
import InclusionWithDnd from './InclusionWithDnd'
import ExperienceConfirmModal from './ExperienceConfirmModal'
import {
  queueAutosaveOnBlur,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'

const createClientId = () =>
  `inc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const withClientIds = (items) => {
  const list = items?.length ? items : [{ inclusion: '' }]
  return list.map((item) => ({
    inclusion: item.inclusion || '',
    _clientId: item._clientId || createClientId(),
  }))
}

const stripClientIds = (items) =>
  items
    .filter((item) => item.inclusion && item.inclusion.trim() !== '')
    .map((item) => ({ inclusion: item.inclusion }))

const buildPayload = (values) => ({
  experientialInclusions: stripClientIds(values.experientialInclusions),
  digitalInclusions: stripClientIds(values.digitalInclusions),
  hasComplimentarySupport: values.hasComplimentarySupport,
})

const truncateInclusionLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

const valuesFromQuest = (questDetail) => ({
  experientialInclusions: withClientIds(questDetail?.experientialInclusions),
  digitalInclusions: withClientIds(questDetail?.digitalInclusions),
  hasComplimentarySupport:
    questDetail?.hasComplimentarySupport !== undefined
      ? questDetail.hasComplimentarySupport
      : true,
})

/**
 * Option 2 queue: blur autosave + follow-up; fields stay editable during save.
 */
const QuestInclusions = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
}) => {
  const [initialValues, setInitialValues] = useState(() =>
    valuesFromQuest(questDetail),
  )
  const [pendingDelete, setPendingDelete] = useState(null)
  const formikRef = useRef(null)
  const valuesRef = useRef(initialValues)
  const busy = !!isFormSubmitting

  useEffect(() => {
    const next = valuesFromQuest(questDetail)
    setInitialValues(next)
    valuesRef.current = next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questDetail?._id, questDetail?.id])

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        await runQueuedFormikSave({
          values,
          valuesRef,
          buildPayload,
          resetForm,
          setInitialValues,
          save: (payload) => updateQuestDetails(payload, { silent: true }),
        })
      }}>
      {(formik) => {
        formikRef.current = formik
        valuesRef.current = formik.values
        const { values, setFieldValue, handleSubmit, dirty } = formik
        const fieldBlur = () => {
          queueAutosaveOnBlur(formik)
        }

        const persist = (nextValues) => {
          updateQuestDetails(buildPayload(nextValues), { silent: true })
        }

        const moveInclusion = (fieldName) => (dragIndex, hoverIndex) => {
          const list = [...formikRef.current.values[fieldName]]
          const [moved] = list.splice(dragIndex, 1)
          list.splice(hoverIndex, 0, moved)
          setFieldValue(fieldName, list, false)
        }

        const persistAfterReorder = () => {
          if (!formikRef.current) return
          persist(formikRef.current.values)
        }

        const deleteInclusion = (fieldName, index) => {
          const current = formikRef.current.values[fieldName]
          const remaining = current.filter((_, i) => i !== index)
          const nextList = remaining.length
            ? remaining
            : [{ inclusion: '', _clientId: createClientId() }]
          const nextValues = {
            ...formikRef.current.values,
            [fieldName]: nextList,
          }
          setFieldValue(fieldName, nextList, false)
          persist({ ...nextValues, [fieldName]: remaining })
        }

        const requestDeleteInclusion = (fieldName, index) => {
          const label =
            formikRef.current?.values?.[fieldName]?.[index]?.inclusion || ''
          setPendingDelete({ fieldName, index, label })
        }

        const confirmDeleteInclusion = () => {
          if (!pendingDelete) return
          deleteInclusion(pendingDelete.fieldName, pendingDelete.index)
          setPendingDelete(null)
        }

        const deleteMessage = (() => {
          if (!pendingDelete) return null
          const name = truncateInclusionLabel(pendingDelete.label)
          return name
            ? `\u201C${name}\u201D will be removed from the Prosperity page.`
            : 'This inclusion will be removed from the Prosperity page.'
        })()

        const renderColumn = (title, fieldName) => (
          <div>
            <h5>{title}</h5>
            <FieldArray name={fieldName}>
              {({ push }) => (
                <div className='experience-lrow-list'>
                  {values[fieldName].map((item, index) => (
                    <InclusionWithDnd
                      key={item._clientId}
                      dndType={fieldName.toUpperCase()}
                      index={index}
                      name={`${fieldName}.${index}.inclusion`}
                      canDelete={values[fieldName]?.length > 1}
                      onDelete={() => requestDeleteInclusion(fieldName, index)}
                      moveItem={moveInclusion(fieldName)}
                      onDragEnd={persistAfterReorder}
                      onBlur={fieldBlur}
                    />
                  ))}
                  <button
                    type='button'
                    className='experience-btn-sm btn-outline-experience'
                    onClick={() =>
                      push({ inclusion: '', _clientId: createClientId() })
                    }>
                    Add
                  </button>
                </div>
              )}
            </FieldArray>
          </div>
        )

        return (
          <>
            <form onSubmit={handleSubmit} aria-busy={busy || undefined}>
              <FormikDirtyReporter id='content-inclusions' dirty={dirty} />
              <div className='experience-twocol'>
                {renderColumn(
                  'Experiential Inclusions',
                  'experientialInclusions',
                )}
                {renderColumn('Digital Inclusions', 'digitalInclusions')}
              </div>
              <div className='experience-check mt-12px'>
                <ExperienceCheckbox
                  name='hasComplimentarySupport'
                  checked={!!values.hasComplimentarySupport}
                  label={
                    <>
                      This experience has complementary support, i.e., pre and
                      post coaching sessions.
                    </>
                  }
                  onChange={(e) => {
                    const next = e.target.checked
                    setFieldValue('hasComplimentarySupport', next, false)
                    persist({
                      ...values,
                      hasComplimentarySupport: next,
                    })
                  }}
                />
              </div>
              <button type='submit' hidden aria-hidden />
            </form>
            <ExperienceConfirmModal
              show={pendingDelete != null}
              onHide={() => setPendingDelete(null)}
              onConfirm={confirmDeleteInclusion}
              title='Delete this inclusion?'
              message={deleteMessage}
              cancelLabel='Cancel'
              confirmLabel='Delete'
              isLoading={busy}
            />
          </>
        )
      }}
    </Formik>
  )
}

export default QuestHOC(QuestInclusions)
